import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { railtelGetSubscriberUsage } from "../../api/railtelApi";
import "./InternetPlanCard.css"; // shared .mac-modal-overlay / .mac-modal chrome
import "./RailtelSessionHistoryModal.css";

/**
 * RailTel's /sessions endpoint doesn't take a date-range param like the
 * OneRADIUS session-history API does — it just returns whatever window
 * the provider itself tracks, so there's no From/To picker here, just a
 * refresh button.
 *
 * Session rows come back with these fields directly now (the provider's
 * new API returns clean structured JSON — Start Time, End Time, Session
 * Time, Upload, Download, Total, MAC, Framed-IP, Termination Cause — no
 * more scraped-table parsing needed, extractRailtelTables() is gone).
 * Upload/Download/Total are already plain MB numbers as strings.
 */
const RailtelSessionHistoryModal = ({ open, username, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);

  useEffect(() => {
    if (open) {
      loadSessions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const { data } = await railtelGetSubscriberUsage(username);
      if (data?.success) {
        setSessions(data.sessions || []);
      } else {
        toast.error("Unable to load session history");
      }
    } catch (err) {
      toast.error("Unable to load session history");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const toNumber = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
  };

  const formatMb = (mb) => {
    if (mb >= 1024 * 1024) return `${(mb / (1024 * 1024)).toFixed(2)} TB`;
    if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
    return `${mb.toFixed(2)} MB`;
  };

  const summary = sessions.reduce(
    (acc, item) => {
      acc.upload += toNumber(item.Upload);
      acc.download += toNumber(item.Download);
      acc.total += toNumber(item.Total);
      return acc;
    },
    { upload: 0, download: 0, total: 0 }
  );

  return (
    <div className="mac-modal-overlay">
      <div className="mac-modal" style={{ width: "95%", maxWidth: "min(1400px, 95vw)" }}>
        <div className="mac-header railtel-session-header">
          <h2>RailTel Session History</h2>
          <button onClick={onClose}>✕</button>
        </div>

        <div style={{ padding: 20 }}>
          <div className="session-toolbar">
            <button className="railtel-btn railtel-btn--outline" onClick={loadSessions} disabled={loading}>
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>

          <div className="session-summary">
            <div className="session-card">
              <small>Total Sessions</small>
              <h2>{sessions.length}</h2>
            </div>
            <div className="session-card">
              <small>Upload</small>
              <h2>{formatMb(summary.upload)}</h2>
            </div>
            <div className="session-card">
              <small>Download</small>
              <h2>{formatMb(summary.download)}</h2>
            </div>
            <div className="session-card">
              <small>Total Usage</small>
              <h2>{formatMb(summary.total)}</h2>
            </div>
          </div>

          <div className="session-table-wrapper">
            <table className="session-table">
              <thead>
                <tr>
                  <th>Start Time</th>
                  <th>End Time</th>
                  <th>Session Time</th>
                  <th>MAC</th>
                  <th>Framed-IP</th>
                  <th>Upload (MB)</th>
                  <th>Download (MB)</th>
                  <th>Total (MB)</th>
                  <th>Termination</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {sessions.length === 0 && (
                  <tr>
                    <td colSpan="10" style={{ textAlign: "center" }}>
                      No Sessions
                    </td>
                  </tr>
                )}
                {sessions.map((item, index) => (
                  <tr key={index}>
                    <td data-label="Start Time">{item["Start Time"]}</td>
                    <td data-label="End Time" className={`${item["End Time"] === "Disconnect/End-Session" ? "railtel-session-online" : ""}`}>
                      {item["End Time"] === "Disconnect/End-Session" ? "Online" : item["End Time"]}
                    </td>
                    <td data-label="Session Time">{item["Session Time"]}</td>
                    <td data-label="MAC">{item["MAC"]}</td>
                    <td data-label="Framed-IP">{item["Framed-IP"]}</td>
                    <td data-label="Upload">{item["Upload"]}</td>
                    <td data-label="Download">{item["Download"]}</td>
                    <td data-label="Total">{item["Total"]}</td>
                    <td data-label="Termination">{item["Termination Cause"] || "—"}</td>
                    <td data-label="Details">
                      <button
                        className="internet-btn-secondary"
                        onClick={() => setSelectedSession(item)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selectedSession && (
        <div className="mac-modal-overlay">
          <div className="mac-modal" style={{ maxWidth: 600 }}>
            <div className="mac-header railtel-session-header">
              <h2>Session Details</h2>
              <button onClick={() => setSelectedSession(null)}>✕</button>
            </div>
            <div style={{ padding: 20 }}>
              <table className="details-table">
                <tbody>
                  {Object.entries(selectedSession).map(([key, value]) => (
                    <tr key={key}>
                      <td>{key}</td>
                      <td>{value || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RailtelSessionHistoryModal;
